import { supabase } from '../lib/supabase';

const UNLOCK_COST = 100;

export interface SongLibraryEntry {
  id: string;
  user_id: string;
  song_id: string;
  unlocked_at: string;
  cost: number;
}

export class SongLibraryService {
  private libraryCache: Set<string> | null = null;
  private cacheUserId: string | null = null;

  async getUserLibrary(userId: string): Promise<Set<string>> {
    if (this.libraryCache && this.cacheUserId === userId) {
      return this.libraryCache;
    }

    const { data, error } = await supabase
      .from('song_library')
      .select('song_id')
      .eq('user_id', userId);

    if (error) throw error;

    const songIds = new Set((data || []).map(entry => entry.song_id));
    this.libraryCache = songIds;
    this.cacheUserId = userId;
    return songIds;
  }

  async isSongUnlocked(userId: string, songId: string): Promise<boolean> {
    const library = await this.getUserLibrary(userId);
    return library.has(songId);
  }

  async unlockSong(userId: string, songId: string): Promise<void> {
    const { data: coins, error: coinsError } = await supabase
      .from('user_coins')
      .select('id, balance')
      .eq('user_id', userId)
      .single();

    if (coinsError) throw coinsError;

    if ((coins.balance || 0) < UNLOCK_COST) {
      throw new Error('Insufficient tokens');
    }

    const newBalance = coins.balance - UNLOCK_COST;
    const { error: updateError } = await supabase
      .from('user_coins')
      .update({ balance: newBalance })
      .eq('id', coins.id);

    if (updateError) throw updateError;

    const { error: txError } = await supabase
      .from('coin_transactions')
      .insert({
        user_id: userId,
        amount: -UNLOCK_COST,
        transaction_type: 'purchase',
        description: 'Song unlock',
        reference_id: songId,
      });

    if (txError) throw txError;

    const { error: insertError } = await supabase
      .from('song_library')
      .insert({
        user_id: userId,
        song_id: songId,
        cost: UNLOCK_COST,
      });

    if (insertError) throw insertError;

    this.libraryCache?.add(songId);
  }

  async hasAnyRecordings(userId: string): Promise<boolean> {
    const { count, error } = await supabase
      .from('recordings')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (error) throw error;
    return (count || 0) > 0;
  }

  getUnlockCost(): number {
    return UNLOCK_COST;
  }

  clearCache(): void {
    this.libraryCache = null;
    this.cacheUserId = null;
  }
}
